import { useEffect, useState, useMemo } from "react";
import { getJSON } from "../services/api";
import SimuladorMeta from "../components/SimuladorMeta";
import InsightsProduto from "../components/InsightsProduto";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { GroupGuard } from "../auth/GroupGuard";

const REQUIRED_GROUPS = [
  "bcba776b-ad66-4c3c-b253-a10a1b65c294", // Admins - TI
  "ccf4a2fb-4ccb-4292-ba83-6c565146e4f0", // Users - TI
  "1c4b0acb-3657-48c1-9024-0904307a94b7", // CEP - Admins
  "c7b622a4-bdb6-48b4-b39e-9f0970a9602f", // CEP - Users
];

// 🚀 Aproximação da função erro (erf) de Abramowitz & Stegun
function erf(x: number) {
  const sign = x >= 0 ? 1 : -1;
  x = Math.abs(x);

  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const t = 1.0 / (1.0 + p * x);
  const y =
    1.0 -
    (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) *
      t *
      Math.exp(-x * x);

  return sign * y;
}

// 🚀 CDF da normal padrão
function normalCDF(z: number) {
  return (1.0 + erf(z / Math.sqrt(2))) / 2.0;
}

type RankingItem = {
  cd_produto: number;
  nome_produto: string;
  media_geral: number;
  desvio_medio: number;
  meses_medidos: number;
  cv_percent: number;
};

export default function HistoricoProdutos() {
  const [ranking, setRanking] = useState<RankingItem[]>([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState<RankingItem | null>(null);
  const [historico, setHistorico] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [busca, setBusca] = useState("");
  const [ordem, setOrdem] = useState<{ col: keyof RankingItem; asc: boolean }>({
    col: "desvio_medio",
    asc: false,
  });


  // 🚀 Simulador
  const [metaSelecionada, setMetaSelecionada] = useState<number | null>(null);
  const [novaMeta, setNovaMeta] = useState<number | null>(null);
  
  type ResultadoSimulacao = {
  mediaHistorica: number;
  meta: number;
  diferenca: number;
  sigma: number;
  riscoHistAbaixo: number;
  riscoHistAcima: number;
  riscoMetaAbaixo: number;
  riscoMetaAcima: number;
};

  const [resultadoSimulacao, setResultadoSimulacao] = useState<ResultadoSimulacao | null>(null);
  const anoAtual = new Date().getFullYear();
  const [mesInicio, setMesInicio] = useState(`${anoAtual}-01`);
  const [mesFim, setMesFim] = useState(`${anoAtual}-12`);

  // 🚀 Carrega ranking histórico ao abrir a tela
  useEffect(() => {
    const params = new URLSearchParams({
      mesInicio,
      mesFim,
    });

    setLoading(true);

    getJSON(`/historico/ranking?${params.toString()}`)
    .then((d) => setRanking(d || []))
    .finally(() => setLoading(false));
  }, [mesInicio, mesFim]);

  // 🚀 Carrega evolução mensal quando seleciona produto
  useEffect(() => {
    if (!produtoSelecionado) return;
    setLoading(true);

    const params = new URLSearchParams({
      cdProduto: String(produtoSelecionado.cd_produto),
      mesInicio,
      mesFim,
    });

    getJSON(`/historico/produto?${params.toString()}`)
      .then((d) => setHistorico(d || []))
      .finally(() => setLoading(false));
  }, [produtoSelecionado, mesInicio, mesFim]);

  // 🚀 Gera insights básicos
  function gerarInsights() {
    if (!produtoSelecionado || !historico.length) return [];
    const desvios = historico.map((h) => h.desvio);
    const mediaDesvio = desvios.reduce((a, b) => a + b, 0) / desvios.length;

    const insights: string[] = [];
    if (mediaDesvio > 2) {
      insights.push(
        `⚠️ O produto ${produtoSelecionado.nome_produto} apresenta alta variabilidade (σ médio ${mediaDesvio.toFixed(
          2
        )}g). Revisar o processo de envase.`
      );
    } else if (mediaDesvio < 0.5) {
      insights.push(
        `✅ O produto ${produtoSelecionado.nome_produto} apresenta ótima consistência (σ médio ${mediaDesvio.toFixed(
          2
        )}g).`
      );
    }

    const ultimos3 = historico.slice(-3).map((h) => h.desvio);
    if (ultimos3.length === 3 && ultimos3[2] > ultimos3[0]) {
      insights.push(
        `📈 Tendência de piora: o desvio aumentou nos últimos 3 meses (${ultimos3.join(
          " → "
        )}).`
      );
    }

    return insights;
  }

  // 🚀 Simulação de risco
const rodarSimulacao = () => {
  if (!produtoSelecionado || !novaMeta) return;

  const mediaHist = produtoSelecionado.media_geral; // média histórica
  const sigma = produtoSelecionado.desvio_medio;

  // --- Cálculo baseado na média histórica ---
  const zHistAbaixo = (95 - mediaHist) / sigma;
  const zHistAcima = (100 - mediaHist) / sigma;

  const riscoHistAbaixo = normalCDF(zHistAbaixo) * 100;
  const riscoHistAcima = (1 - normalCDF(zHistAcima)) * 100;

  // --- Cálculo baseado na nova meta ---
  const zMetaAbaixo = (95 - novaMeta) / sigma;
  const zMetaAcima = (100 - novaMeta) / sigma;

  const riscoMetaAbaixo = normalCDF(zMetaAbaixo) * 100;
  const riscoMetaAcima = (1 - normalCDF(zMetaAcima)) * 100;

  setResultadoSimulacao({
    mediaHistorica: mediaHist,
    meta: novaMeta,
    diferenca: novaMeta - mediaHist,
    sigma,
    riscoHistAbaixo,
    riscoHistAcima,
    riscoMetaAbaixo,
    riscoMetaAcima,
  });
};

  // 🚀 Aplica busca e ordenação
  const dadosFiltrados = ranking
    .filter((r) =>
      busca ? r.nome_produto.toLowerCase().includes(busca.toLowerCase()) : true
    )
    .sort((a, b) => {
      const valA = a[ordem.col];
      const valB = b[ordem.col];
      if (valA < valB) return ordem.asc ? -1 : 1;
      if (valA > valB) return ordem.asc ? 1 : -1;
      return 0;
    });

  function toggleOrdem(col: keyof RankingItem) {
    setOrdem((prev) => ({
      col,
      asc: prev.col === col ? !prev.asc : true,
    }));
  }

  // 🚀 Resumo global de todos os SKUs
  const resumoGlobal = useMemo(() => {
    if (dadosFiltrados.length === 0) return null;

    const totalMedia = dadosFiltrados.reduce((sum, r) => sum + r.media_geral, 0);
    const totalDesvio = dadosFiltrados.reduce((sum, r) => sum + r.desvio_medio, 0);

    const mediaGeral = totalMedia / dadosFiltrados.length;
    const desvioMedio = totalDesvio / dadosFiltrados.length;
    const cvPercent = (desvioMedio / mediaGeral) * 100;

    return { mediaGeral, desvioMedio, cvPercent };
  }, [dadosFiltrados]);

  return (
    <GroupGuard allowedGroups={REQUIRED_GROUPS}>
    <div className="space-y-8">
      {/* 📊 Ranking histórico */}
      <div className="bg-white rounded-xl shadow p-4">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold">Histórico dos produtos</h2>
          <div className="flex gap-2 items-center">
            <input
              type="text"
              placeholder="🔎 Produto desejado ..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            <input
              type="month"
              value={mesInicio}
              onChange={(e) => setMesInicio(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            <span>até</span>
            <input
              type="month"
              value={mesFim}
              onChange={(e) => setMesFim(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          {resumoGlobal && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700">
              📊 <b>Resumo de todos os SKUs:</b>{" "}
              Média geral = {resumoGlobal.mediaGeral.toFixed(2)} g,{" "}
              σ médio = {resumoGlobal.desvioMedio.toFixed(2)} g,{" "}
              CV = {resumoGlobal.cvPercent.toFixed(2)}%
            </div>
          )}
          <table className="min-w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-slate-100 text-left">
                <th className="px-3 py-2 cursor-pointer" onClick={() => toggleOrdem("nome_produto")}>
                  Produto {ordem.col === "nome_produto" && (ordem.asc ? "▲" : "▼")}
                </th>
                <th className="px-3 py-2 cursor-pointer" onClick={() => toggleOrdem("media_geral")}>
                  Média Geral {ordem.col === "media_geral" && (ordem.asc ? "▲" : "▼")}
                </th>
                <th className="px-3 py-2 cursor-pointer" onClick={() => toggleOrdem("desvio_medio")}>
                  Desvio Médio (σ) {ordem.col === "desvio_medio" && (ordem.asc ? "▲" : "▼")}
                </th>
                <th className="px-3 py-2">
                  CV (%) 
                  <span title="Coeficiente de Variação: (σ ÷ média) × 100">
                    ⓘ
                  </span>
                </th>
                <th className="px-3 py-2 cursor-pointer" onClick={() => toggleOrdem("meses_medidos")}>
                  Meses Medidos {ordem.col === "meses_medidos" && (ordem.asc ? "▲" : "▼")}
                </th>
              </tr>
            </thead>
            <tbody>
              {dadosFiltrados.map((r) => (
                <>
                  <tr
                    key={r.cd_produto}
                    className={`border-b hover:bg-slate-50 cursor-pointer ${
                      produtoSelecionado?.cd_produto === r.cd_produto
                        ? "bg-slate-100 font-semibold"
                        : "odd:bg-white even:bg-slate-50"
                    }`}
                    onClick={() =>
                      setProdutoSelecionado(
                        produtoSelecionado?.cd_produto === r.cd_produto ? null : r
                      )
                    }
                  >
                    <td className="px-3 py-2">{r.nome_produto}</td>
                    <td className="px-3 py-2">{r.media_geral}</td>
                    <td className="px-3 py-2">{r.desvio_medio}</td>
                    <td>
                      {r.media_geral
                        ? ((r.desvio_medio / r.media_geral) * 100).toFixed(2) + "%"
                        : "-"}
                    </td>
                    <td className="px-3 py-2">{r.meses_medidos}</td>
                  </tr>

                  {/* 🔽 Row detail expandido */}
                  {produtoSelecionado?.cd_produto === r.cd_produto && (
                    <tr>
                      <td colSpan={4} className="bg-slate-50 p-4">
                        {loading ? (
                          <div className="text-slate-500">Carregando...</div>
                        ) : (
                          <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={historico}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="mes_ref" />
                              <YAxis domain={[90, 105]} ticks={Array.from({ length: 16 }, (_, i) => 90 + i)}/>
                              <Tooltip labelFormatter={(mes: string) => `Mês: ${mes}`} />
                              <Line type="monotone" dataKey="media" stroke="#2563eb" strokeWidth={2} name="Média" dot={false} />
                              <Line type="monotone" dataKey="minimo" stroke="#16a34a" strokeWidth={1.5} name="Mínimo" dot={false} />
                              <Line type="monotone" dataKey="maximo" stroke="#dc2626" strokeWidth={1.5} name="Máximo" dot={false} />
                              <Line type="monotone" dataKey="desvio" stroke="#f97316" strokeDasharray="4 2" name="Desvio (σ)" dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        )}

                        {/* 💡 Insights */}
                        <div className="mt-4 space-y-2">
                          {gerarInsights().map((txt, idx) => (
                            <div key={idx} className="p-2 bg-slate-200 rounded text-sm text-slate-700">
                              {txt}
                            </div>
                          ))}
                        </div>

                        {/* 🎯 Simulador de Meta */}
                        <SimuladorMeta
                          mediaHist={produtoSelecionado.media_geral}
                          sigma={produtoSelecionado.desvio_medio}
                          onChangeMeta={(meta) => setMetaSelecionada(meta)} // 👈 callback
                        />
                        
                          {/* 📘 Explicação didática */}
                          <div className="mt-4 text-xs text-slate-500 leading-relaxed">
                            📘 <b>Como calculamos:</b> usamos a distribuição normal (Gaussiana).  
                            <br />
                            • <b>σ</b> é o desvio padrão histórico do produto.  
                            <br />
                            • Fórmula: <i>Z = (limite − média) / σ</i>.  
                            <br />
                            • Probabilidades são obtidas pela função de distribuição acumulada (CDF).
                          </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
          {/* 📘 Notas explicativas */}
          <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded text-sm text-slate-700 leading-relaxed">
            <b>ℹ️ Como interpretar:</b>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>
                <b>Desvio Médio (σ):</b> mostra a variação <u>absoluta</u> em gramas em torno da média do produto. Útil para avaliar a estabilidade de cada item isolado.
              </li>
              <li>
                <b>CV (%):</b> mostra a variação <u>relativa</u> (σ ÷ média × 100). Útil para comparar produtos de pesos diferentes, pois normaliza pela média.
              </li>
              <li>
                Em resumo: σ ajuda a entender a variação real em gramas, enquanto o CV% permite avaliar e comparar a consistência entre produtos distintos.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    </GroupGuard>
  );
}
